import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { checkIsSsr } from '@shared/utils/check-is-ssr';

import { ErrorFetch } from './errors';

// A stale/invalid apikey is still truthy, so route guards (`isAuthenticated`)
// would otherwise keep letting the user back in after this redirect, bouncing
// forever between /signin and /dashboard. Clearing it here is what breaks the loop.
const handleUnauthorized = (error: unknown, meta?: Record<string, unknown>) => {
  // No browser to redirect on the server, and `window` is undefined there —
  // touching it would throw out of this side-effect callback and could crash
  // the process. Let the loader's catch handle the failure; the client-side
  // route guards perform the actual /signin redirect after hydration.
  if (checkIsSsr()) {
    return;
  }

  if (!(error instanceof ErrorFetch) || error.status !== 401) {
    return;
  }

  // The sign-in form shows its own inline error for a wrong-password 401 and
  // opts out via meta — otherwise this redirect would reload /signin and swallow
  // that error. Every other 401 (stale session, invalid `become` key) still
  // redirects, since nothing else surfaces the failure to the user.
  if (meta?.skipUnauthorizedRedirect) {
    return;
  }

  localStorage.removeItem('user');
  window.location.href = '/signin';
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => handleUnauthorized(error, query.meta),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      handleUnauthorized(error, mutation.meta),
  }),
});
